// src/pages/Users/Inscription/utils/inscriptionUtils.js

import moment from "moment";

export const DEFAULT_FORM_DATA = {
  nom: "",
  prenom: "",
  genre: "",
  date_naissance: "",
  lieu_naissance: "",
  poids: "",
  licence_number: "",
  adresse: "",
  cp: "",
  commune: "",
  representant_legal: "",
  tel: "",
  tel_secondaire: "",
  email: "",
  email_secondaire: "",
  groupeId: "",
  statut_inscription: "",
  autorisation_photo: true,
  belt_id: "",
  date_passage: "",
  notes_adherent: "",
  saison_active: "",
  passsport_number: "",
};

export const COLOR_TOKEN_MAP = {
  "bg-orange-400": {
    light: "bg-orange-100",
    solid: "bg-orange-500",
    border: "border-orange-300",
    text: "text-orange-700",
    ring: "ring-orange-200",
  },
  "bg-indigo-400": {
    light: "bg-indigo-100",
    solid: "bg-indigo-500",
    border: "border-indigo-300",
    text: "text-indigo-700",
    ring: "ring-indigo-200",
  },
  "bg-purple-400": {
    light: "bg-purple-100",
    solid: "bg-purple-500",
    border: "border-purple-300",
    text: "text-purple-700",
    ring: "ring-purple-200",
  },
  "bg-green-400": {
    light: "bg-green-100",
    solid: "bg-green-500",
    border: "border-green-300",
    text: "text-green-700",
    ring: "ring-green-200",
  },
  "bg-yellow-400": {
    light: "bg-yellow-100",
    solid: "bg-yellow-500",
    border: "border-yellow-300",
    text: "text-yellow-800",
    ring: "ring-yellow-200",
  },
  "bg-blue-400": {
    light: "bg-blue-100",
    solid: "bg-blue-500",
    border: "border-blue-300",
    text: "text-blue-700",
    ring: "ring-blue-200",
  },
  "bg-red-400": {
    light: "bg-red-100",
    solid: "bg-red-500",
    border: "border-red-300",
    text: "text-red-700",
    ring: "ring-red-200",
  },
  "bg-pink-400": {
    light: "bg-pink-100",
    solid: "bg-pink-500",
    border: "border-pink-300",
    text: "text-pink-700",
    ring: "ring-pink-200",
  },
  "bg-teal-400": {
    light: "bg-teal-100",
    solid: "bg-teal-500",
    border: "border-teal-300",
    text: "text-teal-700",
    ring: "ring-teal-200",
  },
  "bg-cyan-500": {
    light: "bg-cyan-100",
    solid: "bg-cyan-500",
    border: "border-cyan-300",
    text: "text-cyan-700",
    ring: "ring-cyan-200",
  },
  "bg-lime-400": {
    light: "bg-lime-100",
    solid: "bg-lime-500",
    border: "border-lime-300",
    text: "text-lime-700",
    ring: "ring-lime-200",
  },
  "bg-fuchsia-500": {
    light: "bg-fuchsia-100",
    solid: "bg-fuchsia-500",
    border: "border-fuchsia-300",
    text: "text-fuchsia-700",
    ring: "ring-fuchsia-200",
  },
  "bg-amber-600": {
    light: "bg-amber-100",
    solid: "bg-amber-600",
    border: "border-amber-300",
    text: "text-amber-800",
    ring: "ring-amber-200",
  },
  "bg-gray-500": {
    light: "bg-gray-100",
    solid: "bg-gray-500",
    border: "border-gray-300",
    text: "text-gray-700",
    ring: "ring-gray-200",
  },
};

export function toBool(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function formatPrenom(prenom = "") {
  return String(prenom)
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");
}

export function getGroupAmount(group) {
  if (!group) return 0;

  return (
    toNumber(group.tarif_licence, 0) +
    toNumber(group.tarif_cotisation, 0)
  );
}

export function getHighestPricedGroup(selectedGroups = []) {
  if (!Array.isArray(selectedGroups) || !selectedGroups.length) {
    return null;
  }

  return selectedGroups.reduce((highestGroup, currentGroup) => {
    const highestAmount = getGroupAmount(highestGroup);
    const currentAmount = getGroupAmount(currentGroup);

    return currentAmount > highestAmount
      ? currentGroup
      : highestGroup;
  }, selectedGroups[0]);
}

export function getHighestGroupAmount(selectedGroups = []) {
  const highestPricedGroup = getHighestPricedGroup(selectedGroups);

  return getGroupAmount(highestPricedGroup);
}

export function formatEuro(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

export function getColorMeta(color = "") {
  return (
    COLOR_TOKEN_MAP[color] || {
      light: "bg-slate-100",
      solid: "bg-slate-500",
      border: "border-slate-300",
      text: "text-slate-700",
      ring: "ring-slate-200",
    }
  );
}

export function getGroupSchedules(group) {
  return [
    group?.horaire1,
    group?.horaire2,
    group?.horaire3,
    group?.horaire4,
    group?.horaire5,
    group?.horaire6,
  ].filter(Boolean);
}

export function getGroupTeachers(group) {
  return [
    group?.enseignant1,
    group?.enseignant2,
    group?.enseignant3,
    group?.enseignant4,
    group?.enseignant5,
    group?.enseignant6,
  ].filter(Boolean);
}

export function getGroupQuota(group) {
  const members = toNumber(group?.nombreAdherents, 0);
  const quota = toNumber(group?.quota_groupe, 0);
  const hasQuota = quota > 0;
  const percent = hasQuota
    ? Math.min(100, Math.round((members / quota) * 100))
    : 0;
  const isFull = hasQuota && members >= quota;

  return {
    members,
    quota,
    hasQuota,
    percent,
    isFull,
    remaining: hasQuota ? Math.max(0, quota - members) : null,
  };
}

export function getGroupUnavailableReason(group, isQuotaActive) {
  const inscriptionActive =
    group?.inscription_active === undefined
      ? true
      : toBool(group?.inscription_active);

  if (!inscriptionActive) {
    return "Non ouvert à l'inscription";
  }

  const quota = getGroupQuota(group);

  if (isQuotaActive && quota.isFull) {
    return "Quota dépassé sur ce groupe";
  }

  return "";
}

export function canRegisterToGroup(group, isQuotaActive) {
  return !getGroupUnavailableReason(group, isQuotaActive);
}

export function getGroupWarning(group, isQuotaActive) {
  const quota = getGroupQuota(group);

  if (!isQuotaActive && quota.isFull) {
    return `Le groupe a atteint son quota de ${quota.quota} adhérents, mais l’inscription reste autorisée.`;
  }

  return "";
}

export function normalizeGroupYears(value = "") {
  const raw = String(value || "").trim();

  if (!raw) return [];

  if (raw.startsWith("+")) return [raw];

  return raw
    .split(/[-, ]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function validateAndFormatDate(dateStr) {
  const cleaned = String(dateStr || "").replace(/[^0-9/]/g, "");
  const formats = ["DD/MM/YY", "DDMMYY", "DDMMYYYY", "DD/MM/YYYY"];

  for (const format of formats) {
    const momentDate = moment(cleaned, format, true);

    if (momentDate.isValid() && !momentDate.isAfter(moment())) {
      return momentDate.format("YYYY-MM-DD");
    }
  }

  return null;
}

export function formatBirthDateInput(value = "") {
  let formattedValue = String(value).replace(/[^\d/]/g, "");

  if (formattedValue.length === 2 || formattedValue.length === 5) {
    if (formattedValue.slice(-1) !== "/") {
      formattedValue += "/";
    }
  }

  if (formattedValue.length > 10) {
    formattedValue = formattedValue.slice(0, 10);
  }

  return formattedValue;
}

export function isValidBirthDateInput(value = "") {
  if (String(value).length !== 10) return true;

  const momentDate = moment(value, "DD/MM/YYYY", true);

  return momentDate.isValid() && !momentDate.isAfter(moment());
}

export function buildDojosById(dojos = []) {
  return dojos.reduce((acc, dojo) => {
    acc[String(dojo.id)] = dojo;
    return acc;
  }, {});
}

export function buildInscriptionPayload({
  formData,
  selectedGroups,
  saisonActive,
}) {
  const photo_url = formData.autorisation_photo ? "" : "/img/interdiction.jpg";

  return {
    ...formData,
    date_naissance: validateAndFormatDate(formData.date_naissance),
    groupes: selectedGroups.map((group) => group.id),
    photo_url,
    belt_id: formData.belt_id,
    date_passage: formData.date_passage,
    saison_active: saisonActive,
  };
}

export function validateInscriptionBeforeSubmit({
  formData,
  selectedGroups,
  dateError,
  user,
  isQuotaActive,
}) {
  if (!user?.userId) {
    return "Vous devez être connecté pour effectuer cette inscription.";
  }

  if (!formData.statut_inscription) {
    return "Veuillez sélectionner le type d’inscription.";
  }

  if (!formData.date_naissance || dateError) {
    return "Veuillez corriger la date de naissance avant de soumettre le formulaire.";
  }

  if (!validateAndFormatDate(formData.date_naissance)) {
    return "La date de naissance n’est pas valide.";
  }

  if (!selectedGroups.length) {
    return "Veuillez sélectionner au moins un groupe.";
  }

  const blockedGroup = selectedGroups.find(
    (group) => !canRegisterToGroup(group, isQuotaActive),
  );

  if (blockedGroup) {
    return `Le groupe ${blockedGroup.nom} n’est pas disponible à l’inscription.`;
  }

  if (
    !formData.accept_reglement ||
    !formData.accept_infos_exactes ||
    !formData.accept_responsabilite
  ) {
    return "Veuillez cocher les validations obligatoires avant de finaliser l’inscription.";
  }

  return "";
}
